import { FormInput, Popover, Tooltip, copyTextToClipboard, toast } from '@systemeio/ui'
import {
  BLOGS_API,
  changePageTemplate,
  createPage,
  deletePage,
  duplicatePage,
  updatePage,
} from 'modules/blogs/api/blog-api'
import { useBlogPages } from 'modules/blogs/hooks/use-blog-pages'
import { BlogInterface } from 'modules/blogs/types/blog-interface'
import {
  BlogPagesEditInterface,
  BlogPagesInterface,
} from 'modules/blogs/types/blog-pages-interface'
import { getBlogPageUrl } from 'modules/blogs/utils/get-blog-page-url'
import React, { useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import FormUrlPathInput from 'shared/components/form-url-path-input'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { BlogEntityDuplicateInterface } from '../types/blog-entity-interface'
import BlogEntityDuplicateModal from './blog-entity-duplicate-modal'
import { CheckIcon, CopyIcon, QuestionMarkIcon } from '@systemeio/ui/icons'

interface BlogPagesProps {
  blog?: BlogInterface
  blogId: string
}

const defaultValues: BlogPagesEditInterface = {
  name: '',
  path: '',
}

const defaultErrors: BlogPagesEditInterface = { name: '', path: '' }

const BlogPages = ({ blog, blogId }: BlogPagesProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const { setPagination, pagination, setLimit, limit } = usePagination(true)
  const { blogPages, blogPagesMutate, blogPagesCounter } = useBlogPages({
    limit,
    pagination,
    id: blogId,
  })
  const updateData = useUpdateData({
    api: `${BLOGS_API}/${blogId}/pages`,
    setPagination,
  })

  const [errors, setErrors] = useState<BlogPagesEditInterface>(defaultErrors)

  const onCreate = async (data: BlogPagesEditInterface) => {
    try {
      await fetchWithErrors(async () => {
        if (blog) {
          await createPage(blog?.id, data)
          await updateData(true, blogPages)
        }
      }, setErrors)
      toast.success(t('dashboard.blog.pages.created'))
    } catch (e) {
      throw e
    }
  }

  const onEdit = async (page: BlogPagesInterface, data: BlogPagesEditInterface) => {
    const homePage = blogPages?.items.find(el => el.path === '')
    const postsPage = blogPages?.items.find(el => el.path === 'posts')
    const searchPage = blogPages?.items.find(el => el.path === 'search')

    const isHomePage = page.id === homePage?.id
    const isPostsPage = page.id === postsPage?.id
    const isSearchPage = page.id === searchPage?.id

    const updatedData = isHomePage || isPostsPage || isSearchPage ? { name: data.name } : data
    try {
      await fetchWithErrors(async () => {
        if (blog) {
          const response = await updatePage(page.id, updatedData)
          await updateData(false, undefined, {
            mutationData: blogPages,
            onMutate: async () => {
              await blogPagesMutate(data => {
                if (data) {
                  return {
                    ...data,
                    items: data.items.map(el => {
                      if (el.id === page.id) {
                        return { ...response.data }
                      } else {
                        return el
                      }
                    }),
                  }
                }
              }, false)
            },
          })
        }
      }, setErrors)
    } catch (e) {
      throw e
    }
  }

  const onRemove = async (data: BlogPagesInterface) => {
    try {
      await deletePage(data.id)
      await updateData(false, blogPages)
    } catch (e) {
      throw e
    }
  }

  const duplicateHandler = async (id: number, data: BlogEntityDuplicateInterface) => {
    await duplicatePage(id, data)
    await updateData(false, blogPages)
  }

  const onModalClose = () => setErrors(defaultErrors)

  const columns: ColumnDefinitionInterface<BlogPagesInterface>[] = [
    {
      key: 'name',
      header: t('global.title_caption'),
      widthPercent: 40,
      onRender: blogPage => (
        <div className={'flex w-full items-center gap-1.5 truncate'}>
          <a rel="noreferrer" className="primary-link w-fit truncate" href={blogPage.editorPageUrl}>
            {blogPage.name}
          </a>
          {!blogPage.isEmptyLayout && (
            <Tooltip mode={'hover'} label={t('dashboard.blog.pages.use_blog_layout')}>
              <CheckIcon className={'stroke-green'} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'path',
      header: t('global.url'),
      widthPercent: 40,
      onRender: blogPage => {
        const url = getBlogPageUrl(blog, blogPage)
        return (
          <div className="flex w-full gap-3">
            <Tooltip mode="hover" label={t('global.copy')} className={'overflow-auto'}>
              <button
                onClick={async () => {
                  await copyTextToClipboard(url, t('global.copy_clipboard'))
                }}
                className="group w-full outline-hidden"
              >
                <CopyIcon className="main-transition-colors group-focus-visible:fill-brand-primary hover:fill-brand-primary h-[16px] w-[16px] cursor-pointer sm:h-[20px] sm:w-[20px]" />
              </button>
            </Tooltip>
            <div className="truncate">{url}</div>
          </div>
        )
      },
      onRenderSmall: blogPage => {
        const url = getBlogPageUrl(blog, blogPage)
        return (
          <div className={'flex w-full items-center gap-3'}>
            <span className={'text-text-primary whitespace-nowrap font-bold'}>
              {t('global.url')}
            </span>
            <div className="flex w-fit gap-3 truncate lg:w-full">
              <div className="truncate">{url}</div>
              <Tooltip mode="hover" label={t('global.copy')}>
                <button
                  onClick={async () => {
                    await copyTextToClipboard(url, t('global.copy_clipboard'))
                  }}
                  className="group w-full outline-hidden"
                >
                  <CopyIcon className="group-focus-visible:fill-brand-primary hover:fill-brand-primary h-[16px] w-[16px] cursor-pointer sm:h-[20px] sm:w-[20px]" />
                </button>
              </Tooltip>
            </div>
          </div>
        )
      },
    },
    {
      key: 'createdAt',
      header: t('global.created_on'),
      widthPercent: 20,
      onRender: blogPage => (
        <span>
          {dateTimeFormat(blogPage.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  const changeBlogLayoutUsage = async (blogPage: BlogPagesInterface) => {
    await changePageTemplate(blogPage.id)
    await updateData(false, undefined, {
      mutationData: blogPages,
      onMutate: async () => {
        await blogPagesMutate(data => {
          if (data) {
            return {
              items: data.items.map(item => {
                if (item.id === blogPage.id) item = { ...item, isEmptyLayout: !item.isEmptyLayout }
                return item
              }),
              hasMore: data.hasMore,
            }
          }
        }, false)
      },
    })
  }

  return (
    <ListingLayout
      title={t('dashboard.blog.pages.title_plural')}
      counter={blogPagesCounter}
      data={blogPages}
      type={'compact'}
      createData={{
        onCreate: onCreate,
        onAfterLeave: onModalClose,
        requiredFields: ['path', 'name'],
        buttonCaption: t('dashboard.blog.pages.create_blog_page'),
        fullCaption: t('dashboard.blog.pages.create_new_blog_page'),
        needToast: false,
        defaultValues,
        creatable: [
          {
            field: 'name',
            onRender: (value, onChange) => (
              <FormInput
                key={'name-create'}
                value={value}
                onChange={e => onChange(e.target.value)}
                label={t('dashboard.blog.pages.blog_page_name')}
                error={errors.name}
              />
            ),
          },
          {
            field: 'path',
            onRender: (value, onChange) => (
              <FormUrlPathInput
                key={'path-create'}
                label={
                  <div className={'flex items-center gap-1'}>
                    {t('global.url_path')}
                    <Popover
                      popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                      label={t('dashboard.funnel.configuration.webinar.url_path_popover-2')}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                domainName={blog?.domain.domainName}
                additionalPath={blog?.path}
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.path}
              />
            ),
          },
        ],
      }}
    >
      <Table
        caption={t('dashboard.blog.pages.title')}
        actions={{
          openLink: [
            {
              name: 'view',
              openInNew: true,
              setHref: page => getBlogPageUrl(blog, page),
            },
            {
              caption: t('dashboard.actions.title.edit'),
              name: 'edit',
              openInNew: false,
              setHref: page => page.editorPageUrl,
            },
          ],
          custom: [
            {
              name: 'duplicate',
              caption: t('dashboard.actions.title.duplicate'),
              onActionRender: (opened, onClose, page, key) => (
                <BlogEntityDuplicateModal
                  key={key}
                  opened={opened}
                  blogEntity={page}
                  title={t('dashboard.blog.page.duplicate_blog_page')}
                  onClose={onClose}
                  onUpdateData={async () => {
                    onModalClose()
                    onClose()
                  }}
                  duplicateHandler={duplicateHandler}
                />
              ),
            },
            {
              name: 'use-blog-layout',
              caption: t('dashboard.blog.pages.use_blog_layout'),
              shouldRender: blogPage => blogPage.isEmptyLayout,
              onActionRender: (opened, onClose, blogPage, key) => (
                <ConfirmationModal
                  toastCaption={t('dashboard.blog.pages.template_changed')}
                  confirmationText={t('dashboard.blog.pages.use_blog_layout_description')}
                  key={key}
                  opened={opened}
                  onClose={onClose}
                  onConfirm={() => changeBlogLayoutUsage(blogPage)}
                />
              ),
            },
            {
              name: 'dont-use-blog-layout',
              caption: t('dashboard.blog.pages.dont_use_blog_layout'),
              shouldRender: blogPage => !blogPage.isEmptyLayout,
              onActionRender: (opened, onClose, blogPage, key) => (
                <ConfirmationModal
                  confirmationText={t('dashboard.blog.pages.dont_use_blog_layout_description')}
                  toastCaption={t('dashboard.blog.pages.template_changed')}
                  key={key}
                  opened={opened}
                  onClose={onClose}
                  onConfirm={() => changeBlogLayoutUsage(blogPage)}
                />
              ),
            },
          ],
          settings: {
            onEdit,
            onModalClose,
            fullCaption: t('dashboard.blog.pages.edit_pages'),
            editable: [
              {
                field: 'name',
                onRender: (value, onChange) => (
                  <FormInput
                    key={'name-edit'}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    label={t('dashboard.blog.pages.blog_page_name')}
                    error={errors.name}
                  />
                ),
              },
              {
                field: 'path',
                shouldRender: (state, page) => {
                  const homePage = blogPages?.items.find(el => el.path === '')
                  const postsPage = blogPages?.items.find(el => el.path === 'posts')
                  const searchPage = blogPages?.items.find(el => el.path === 'search')
                  return (
                    page.id !== homePage?.id &&
                    postsPage?.id !== page.id &&
                    searchPage?.id !== page.id
                  )
                },
                onRender: (value, onChange) => (
                  <FormUrlPathInput
                    key={'path-edit'}
                    label={
                      <div className={'flex items-center gap-1'}>
                        {t('global.url_path')}
                        <Popover
                          popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                          label={t('dashboard.funnel.configuration.webinar.url_path_popover-2')}
                        >
                          {show => (
                            <QuestionMarkIcon
                              className={`${
                                show ? 'fill-brand-primary' : 'fill-fill-primary'
                              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                            />
                          )}
                        </Popover>
                      </div>
                    }
                    domainName={blog?.domain.domainName}
                    additionalPath={blog?.path}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    error={errors.path}
                  />
                ),
              },
            ],
          },
          remove: {
            shouldRender: page =>
              page.path !== '' && page.path !== 'posts' && page.path !== 'search',
            onChange: onRemove,
          },
        }}
        data={blogPages}
        columns={columns}
        paginator={{ pagination, setPagination }}
        limiter={{ limit, setLimit }}
      />
    </ListingLayout>
  )
}

export default BlogPages
